import React from "react";
import { OriginRoute, DestinyRoute, ITripStopItem } from "@types";
import { Button } from "@rio-cloud/rio-uikit";

interface InputItemProps {
  id: string;
  label: string;
  value: string;
  iconLeft: string;
  hint?: string;
  onCheckpointItem?: () => void;
  disabled?: boolean;
}

const Input: React.FC<InputItemProps> = ({ id, label, value, iconLeft, hint, onCheckpointItem, disabled = false }) => (
  <div className="display-flex align-items-center margin-top-10 margin-bottom-10">
    <div className="width-100pct">
      <div className="display-flex align-items-baseline justify-content-between gap-10 margin-bottom-5">
        <label htmlFor={id} className="margin-bottom-0">
          {label}
        </label>
        {hint && <span className="text-size-12 text-color-dark white-space-nowrap">{hint}</span>}
      </div>
      <div className="input-group gap-15">
        <span className="input-group-addon">
          <span className={iconLeft} />
        </span>
        <input id={id} className="form-control" type="text" placeholder="" disabled value={value} />
        <Button 
          bsStyle={Button.PRIMARY} 
          iconOnly 
          iconName="rioglyph rioglyph-checkboxes" 
          disabled={disabled} 
          onClick={onCheckpointItem} 
        />
      </div>
    </div>
  </div>
);

const formatStopEta = (value: string): string => {
  const [datePart, timePart] = value.split(" ");
  if (!timePart) return value;

  const today = new Date();
  const todayBr = [
    String(today.getDate()).padStart(2, "0"),
    String(today.getMonth() + 1).padStart(2, "0"),
    today.getFullYear(),
  ].join("/");

  return datePart === todayBr ? timePart : `${datePart.slice(0, 5)} ${timePart}`;
};

const buildStopHint = (stop: ITripStopItem): string | undefined => {
  if (stop.actualArrival) return `chegou ${formatStopEta(stop.actualArrival)}`;
  if (stop.estimatedArrival) return `previsão ${formatStopEta(stop.estimatedArrival)}`;
  if (stop.isUnplanned || !stop.routeStopId) return undefined;
  return "previsão --";
};

interface StopsSectionProps {
  origin?: OriginRoute;
  destiny?: DestinyRoute;
  stops?: ITripStopItem[];
  onClickCheckpoint?: (id: string) => void;
}

export const StopsSection: React.FC<StopsSectionProps> = ({ origin, destiny, stops, onClickCheckpoint }) => (
  <div className="padding-y-10 padding-x-15">
    <Input 
      id="trip-stop-origin"
      label="Origem" 
      value={origin?.address?.label || ""} 
      iconLeft="rioglyph rioglyph-start" 
      disabled 
    />
    {stops && stops.length > 0 && (
      <div className="padding-left-20">
        {stops.map((stop, index) => (
          <Input
            key={stop.routeStopId ?? stop.checkpointId ?? `stop-${index}`}
            id={`trip-stop-${index}`}
            label={stop.note}
            value={stop.label}
            hint={buildStopHint(stop)}
            iconLeft="rioglyph rioglyph-pushpin"
            disabled={stop.isUnplanned || !stop.checkpointId}
            onCheckpointItem={() => !stop.isUnplanned && stop.checkpointId && onClickCheckpoint?.(stop.checkpointId)}
          />
        ))}
      </div>
    )}
    <Input 
      id="trip-stop-destiny"
      label="Destino" 
      value={destiny?.address?.label || ""} 
      iconLeft="rioglyph-finish" 
      disabled 
    />
  </div>
);
